import React from "react";
import { Alert } from "../ui/Alert";
import { InfoCircle } from "../icons";

interface BookingFlowNoticeProps {
  /** Bold heading for the notice. */
  readonly title: string;
  /** Explanatory body text. */
  readonly message: string;
  /** Label for the single call-to-action button. */
  readonly actionLabel: string;
  /** Invoked when the action button is clicked. */
  readonly onAction: () => void;
  /** Alert variant; defaults to "warning". */
  readonly variant?: "warning" | "danger" | "success" | "info";
}

/**
 * Shared presentational notice for booking-flow interruptions (e.g. an expired
 * session or a transient verification failure): a titled message with a single
 * call-to-action, built on the shared `Alert` primitive. Copy and behaviour are
 * supplied by the caller so the same component serves recoverable and terminal
 * cases alike.
 */
const BookingFlowNotice: React.FC<BookingFlowNoticeProps> = ({
  title,
  message,
  actionLabel,
  onAction,
  variant = "warning",
}) => (
  <Alert variant={variant} className="gcw:flex gcw:items-start info-alert widget-error-panel gcw:mb-bs-3 gcw:mx-4">
    <div className="gcw:flex gcw:flex-row gcw:items-start gcw:gap-2 gcw:p-4">
      <div className="alert-icon">
        <InfoCircle color="#9a3412" size={16} aria-hidden="true" />
      </div>
      <div className="gcw:flex gcw:flex-col gcw:gap-2">
        <p className="gcw:font-semibold">{title}</p>
        <p>{message}</p>
        <button
          type="button"
          className="gcw:inline-flex gcw:items-center gcw:justify-center gcw:self-start gcw:rounded-2xl gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2"
          onClick={onAction}
        >
          {actionLabel}
        </button>
      </div>
    </div>
  </Alert>
);

export default BookingFlowNotice;
